<template>
  <time :datetime="isoDate" :title="fullDateTime">{{ formattedValue }}</time>
</template>

<script setup>
import { computed } from 'vue'
import { formatDateTime, getBrowserLocale, getBrowserTimezone } from '../../utils/dateFormat'

const props = defineProps({
  /**
   * ISO date string or Date object to format
   */
  value: {
    type: [String, Date],
    required: true,
  },
  /**
   * User's preferred locale ('ru', 'en', etc.)
   * Falls back to browser locale if not provided
   */
  locale: {
    type: String,
    default: null,
  },
  /**
   * User's preferred timezone (e.g., 'Europe/Moscow')
   * Falls back to browser timezone if not provided
   */
  timezone: {
    type: String,
    default: null,
  },
  /**
   * Format type:
   * - 'date' - Full date (29.11.2025)
   * - 'dateShort' - Short date (29 нояб.)
   * - 'dateLong' - Long date (29 ноября 2025 г.)
   * - 'time' / 'timeShort' - Time without seconds (18:00)
   * - 'timeFull' - Time with seconds (18:00:00)
   * - 'datetime' - Datetime without seconds (29.11.2025, 18:00)
   * - 'datetimeFull' - Datetime with seconds (29.11.2025, 18:00:00)
   */
  format: {
    type: String,
    default: 'datetime',
    validator: value =>
      [
        'date',
        'dateShort',
        'dateLong',
        'time',
        'timeShort',
        'timeFull',
        'datetime',
        'datetimeFull',
      ].includes(value),
  },
})

const effectiveLocale = computed(() => props.locale || getBrowserLocale())
const effectiveTimezone = computed(() => props.timezone || getBrowserTimezone())

const isoDate = computed(() => {
  if (!props.value) return ''
  try {
    const date = props.value instanceof Date ? props.value : new Date(props.value)
    return date.toISOString()
  } catch {
    return ''
  }
})

const formattedValue = computed(() => {
  return formatDateTime(props.value, {
    locale: effectiveLocale.value,
    timezone: effectiveTimezone.value,
    format: props.format,
  })
})

// Full datetime for title/tooltip
const fullDateTime = computed(() => {
  return formatDateTime(props.value, {
    locale: effectiveLocale.value,
    timezone: effectiveTimezone.value,
    format: 'datetime',
  })
})
</script>
